import { format } from 'date-fns';
import momentTZ from 'moment-timezone';
import moment from 'moment';
import { flatMap, xor } from 'lodash';
import BigNumber from 'bignumber.js';
import { getAppTimezone, safeNewDate } from '../utils';
import Config from '../../config';
import { sortTypeArgumentsBasedOnTypePriority } from './inference';
function tryJSONParse(str) {
    let result;
    try {
        result = JSON.parse(str);
    }
    catch (e) { }
    return result;
}
export const typeDefinitionMap = new Map();
const typeMap = new Map();
// 生成typeKey
export function genSortedTypeKey(typeAnnotation) {
    const { typeKind, typeNamespace, typeName, typeArguments, properties } = typeAnnotation || {};
    const typeKeyArr = [];
    if (typeKind === 'union') {
        // 联合类型
        if (Array.isArray(typeArguments)) {
            // 按返回的每个具体项排序
            const childTypeArgs = typeArguments
                .map((typeArg) => genSortedTypeKey(typeArg))
                .sort((name1, name2) => (name1 > name2 ? 1 : -1));
            typeKeyArr.push(childTypeArgs.join(' | '));
        }
    }
    else if (typeKind === 'anonymousStructure') {
        // 匿名数据结构
        typeKeyArr.push('{');
        if (Array.isArray(properties)) {
            // 按匿名数据结构的key排序
            const childTypeArgs = properties
                .sort(({ name: name1 }, { name: name2 }) => (name1 > name2 ? 1 : -1))
                .map((typeArg) => {
                const { name: typeArgName, typeAnnotation: typeArgTypeAnnotation } = typeArg || {};
                return `${typeArgName}: ${genSortedTypeKey(typeArgTypeAnnotation)}`;
            });
            typeKeyArr.push(childTypeArgs.join(', '));
        }
        typeKeyArr.push('}');
    }
    else {
        const typeArr = [];
        typeNamespace && typeArr.push(typeNamespace);
        typeName && typeArr.push(typeName);
        const typeKey = typeArr.join('.');
        typeKey && typeKeyArr.push(typeKey);
        if (typeKind === 'generic') {
            typeKeyArr.push('<');
            if (Array.isArray(typeArguments)) {
                // 必须按typeArguments定义的顺序，否则实参位置不对
                const childTypeArgs = typeArguments.map((typeArg) => genSortedTypeKey(typeArg));
                typeKeyArr.push(childTypeArgs.join(', '));
            }
            typeKeyArr.push('>');
        }
    }
    return typeKeyArr.join('');
}
// 生成构造函数
function genConstructor(typeKey, definition, genInitFromSchema) {
    if (typeMap[typeKey]) {
        return typeMap[typeKey];
    }
    else {
        typeDefinitionMap[typeKey] = definition;
        const { typeKind, typeNamespace, typeName, typeArguments, properties } = definition || {};
        let propList = properties;
        if (typeKind === 'generic') {
            // List和Map属于特殊范型
            if (typeNamespace === 'nasl.collection' && ['List', 'Map'].includes(typeName)) {
                return;
            }
            const typeArr = [];
            typeNamespace && typeArr.push(typeNamespace);
            typeName && typeArr.push(typeName);
            const genericTypeKey = typeArr.join('.');
            // 范型定义
            const genericDefinition = typeDefinitionMap[genericTypeKey];
            if (genericDefinition) {
                const { typeParams, properties } = genericDefinition || {};
                if (Array.isArray(properties)) {
                    // 用实参替换形参
                    propList = properties.map((property) => {
                        const actualProp = Object.assign({}, property);
                        const { typeAnnotation } = property || {};
                        // 类型形参
                        const index = typeParams.findIndex((typeParam) => (typeParam === null || typeParam === void 0 ? void 0 : typeParam.name) === (typeAnnotation === null || typeAnnotation === void 0 ? void 0 : typeAnnotation.typeName));
                        if (index !== -1) {
                            actualProp.typeAnnotation = typeArguments[index];
                        }
                        return actualProp;
                    });
                }
            }
        }
        const makeConstructor = ({ genInitFromSchema, genSortedTypeKey, typeDefinitionMap }) => {
            const properties = [];
            if (Array.isArray(propList)) {
                propList.forEach((property) => {
                    var _a;
                    const { name: propertyName, typeAnnotation, defaultCode } = property || {};
                    const defaultValue = defaultCode === null || defaultCode === void 0 ? void 0 : defaultCode.code;
                    const defaultValueType = Object.prototype.toString.call(defaultValue);
                    const typeKey = genSortedTypeKey(typeAnnotation);
                    const typeDefinition = typeDefinitionMap[typeKey];
                    const { concept } = typeDefinition || {};
                    let parsedValue = defaultValue;
                    // 设置成null，才能同步给后端清除该值，但是null对checkbox组件是一种特殊状态
                    if (typeKey === 'nasl.core.Boolean') {
                        parsedValue = defaultValue !== null && defaultValue !== void 0 ? defaultValue : undefined;
                    }
                    if (defaultValueType === '[object String]' &&
                        !['nasl.core.String', 'nasl.core.Text', 'nasl.core.Email'].includes(typeKey) &&
                        concept !== 'Enum' &&
                        !['union'].includes(typeKind)) {
                        // 一些特殊情况，特殊处理成undefined
                        // 1.defaultValue在nasl节点上错误得赋值给了空制符串
                        if ([''].includes(defaultValue)) {
                            parsedValue = undefined;
                        }
                        else {
                            parsedValue = (_a = tryJSONParse(defaultValue)) !== null && _a !== void 0 ? _a : defaultValue;
                        }
                    }
                    if (Object.prototype.toString.call(parsedValue) === '[object String]' && !(defaultCode === null || defaultCode === void 0 ? void 0 : defaultCode.executeCode)) {
                        parsedValue = `\`${parsedValue.replace(/['"`\\]/g, (m) => `\\${m}`)}\``;
                    }
                    const needGenInitFromSchema = typeAnnotation && !['primitive'].includes(typeAnnotation.typeKind);
                    const sortedTypeKey = genSortedTypeKey(typeAnnotation);
                    const item = {
                        propertyName,
                        parsedValue,
                        needGenInitFromSchema,
                        sortedTypeKey,
                    };
                    properties.push(item);
                });
            }
            function ctor(params) {
                const level = params.level;
                const defaultValue = params.defaultValue;
                if (defaultValue && Object.prototype.toString.call(defaultValue) === '[object Object]') {
                    Object.assign(this, defaultValue);
                }
                properties.forEach((item) => {
                    // const code = `return defaultValue?.${item.propertyName} ?? ${item.parsedValue};`;
                    // 👇
                    const code = `var _defaultValue$p, _defaultValue;
            return (_defaultValue$p =
              (_defaultValue = defaultValue) === null || _defaultValue === void 0
                ? void 0
                : _defaultValue.${item.propertyName}) !== null && _defaultValue$p !== void 0
              ? _defaultValue$p
              : ${item.parsedValue};`;
                    // parsedValue是字符串，例如： '(() => { \nconst arr = [\n`a`\n];\nreturn arr;\n})()'，所以只能用new Function...
                    let value = new Function('defaultValue', code)(defaultValue);
                    if (item.needGenInitFromSchema) {
                        value = genInitFromSchema(item.sortedTypeKey, value, level);
                    }
                    this[item.propertyName] = value !== null && value !== void 0 ? value : null;
                });
                Object.defineProperty(this, '$type', {
                    value: typeKey,
                    enumerable: false,
                });
            }
            // ctor设置name
            Object.defineProperty(ctor, 'name', {
                value: 'NaslTypeConstructor',
            });
            return ctor;
        };
        const fn = makeConstructor({
            genInitFromSchema,
            genSortedTypeKey,
            typeDefinitionMap,
        });
        typeMap[typeKey] = fn;
        return fn;
    }
}
// 初始化整个应用的构造器
export function initApplicationConstructor(dataTypesMap, genInitFromSchema) {
    if (dataTypesMap) {
        for (const typeKey in dataTypesMap) {
            genConstructor(typeKey, dataTypesMap[typeKey], genInitFromSchema);
        }
    }
}
// 判断字符串的具体类型
export function judgeStrType(str) {
    const regMap = {
        'nasl.core.Date': /^\d{1,4}(\/|-)\d{1,2}(\/|-)\d{1,2}$/,
        'nasl.core.Time': /^(\d{1,2})(:\d{1,2})?:(\d{1,2})$/,
        // 2021-01-01T05:11:22.010Z 或 2021-01-01 05:11:22
        'nasl.core.DateTime': /^(\d{1,4}(\/|-)\d{1,2}(\/|-)\d{1,2}T(\d{1,2})(:\d{1,2})?:(\d{1,2})\.\d{3}Z)$|^(\d{1,4}(\/|-)\d{1,2}(\/|-)\d{1,2} (\d{1,2})(:\d{1,2})?:(\d{1,2}))$/,
    };
    for (const key in regMap) {
        const reg = regMap[key];
        if (reg.test(str)) {
            return key;
        }
    }
}
// 判断 变量 是否属于 类型
export function isInstanceOf(variable, typeKey) {
    const typeConstructor = typeMap[typeKey];
    const typeDefinition = typeDefinitionMap[typeKey];
    const varStr = Object.prototype.toString.call(variable);
    const { concept, typeKind, typeNamespace, typeName, typeArguments } = typeDefinition || {};
    const isPrimitive = isDefPrimitive(typeKey);
    if (typeKind === 'union') {
        return !!(typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments.some((typeArg) => isInstanceOf(variable, genSortedTypeKey(typeArg))));
    }
    else if (concept === 'Enum') {
        // 枚举
        const { enumItems } = typeDefinition;
        if (Array.isArray(enumItems)) {
            if (varStr === '[object String]') {
                // 当前值在枚举中存在
                // 枚举值支持integer，改为不严格判断
                const enumItemIndex = enumItems.findIndex((enumItem) => variable == enumItem.value);
                return enumItemIndex !== -1;
            }
            else if (varStr === '[object Array]') {
                const enumItemIndex = variable.findIndex((varItem) => !isInstanceOf(varItem.value, genSortedTypeKey(typeDefinition)));
                // 当前枚举数组与定义完全匹配
                return enumItemIndex === -1;
            }
        }
    }
    else if (isPrimitive) {
        const isBigNumber = variable instanceof BigNumber || BigNumber.isBigNumber(variable);
        // 基础类型
        if (varStr === '[object String]') {
            const actualStrType = judgeStrType(variable);
            if (actualStrType) {
                return actualStrType === typeKey;
            }
            else if (['nasl.core.String', 'nasl.core.Binary'].includes(typeKey)) {
                return true;
            }
        }
        else if ((varStr === '[object Number]' || isBigNumber) &&
            ['nasl.core.Long', 'nasl.core.Decimal'].includes(typeKey)) {
            return true;
        }
        else if (varStr === '[object Boolean]' && typeKey === 'nasl.core.Boolean') {
            return true;
        }
    }
    else if (typeKind === 'generic' && typeNamespace === 'nasl.collection') {
        if (!((typeName === 'List' && varStr === '[object Array]') || (typeName === 'Map' && varStr === '[object Object]'))) {
            return false;
        }
        // 特殊范型List/Map
        let keyChecked = true;
        // 期望的值的类型
        const valueTypeArg = typeName === 'List' ? typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[0] : typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[1];
        // Map存在key类型校验不通过校验的情况
        if (typeName === 'Map') {
            // 期望的key类型
            const keyTypeArg = typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[0];
            for (const key in variable) {
                if (!isInstanceOf(key, genSortedTypeKey(keyTypeArg))) {
                    keyChecked = false;
                }
            }
        }
        // key校验通过，再校验value是否符合
        if (keyChecked) {
            if (typeName === 'List' && Array.isArray(variable)) {
                const failedIndex = variable.findIndex((varItem) => !isInstanceOf(varItem, genSortedTypeKey(valueTypeArg)));
                // 当前数组为空或者与定义完全匹配
                return variable.length === 0 || failedIndex === -1;
            }
            else if (typeName === 'Map' && variable) {
                let checked = true;
                for (const key in variable) {
                    const varItem = variable[key];
                    if (!isInstanceOf(varItem, genSortedTypeKey(valueTypeArg))) {
                        checked = false;
                    }
                }
                return checked;
            }
        }
    }
    else if (typeConstructor && variable instanceof typeConstructor) {
        return true;
    }
    return false;
}
// 类型定义是否属于基础类型
const isDefPrimitive = (typeKey) => [
    'nasl.core.Boolean',
    'nasl.core.Long',
    'nasl.core.Decimal',
    'nasl.core.String',
    'nasl.core.Text',
    'nasl.core.Binary',
    'nasl.core.Date',
    'nasl.core.Time',
    'nasl.core.DateTime',
    'nasl.core.Email',
].includes(typeKey);
// 类型定义是否属于字符串大类
export const isDefString = (typeKey) => [
    'nasl.core.String',
    'nasl.core.Text',
    'nasl.core.Binary',
    'nasl.core.Date',
    'nasl.core.Time',
    'nasl.core.DateTime',
    'nasl.core.Email',
].includes(typeKey);
// 类型定义是否属于数字大类
export const isDefNumber = (typeKey) => ['nasl.core.Long', 'nasl.core.Decimal'].includes(typeKey);
// 类型定义是否属于数组
export const isDefList = (typeDefinition) => {
    const { typeKind, typeNamespace, typeName } = typeDefinition || {};
    return typeKind === 'generic' && typeNamespace === 'nasl.collection' && typeName === 'List';
};
// 类型定义是否属于Map
export const isDefMap = (typeDefinition) => {
    const { typeKind, typeNamespace, typeName } = typeDefinition || {};
    return typeKind === 'generic' && typeNamespace === 'nasl.collection' && typeName === 'Map';
};
// 值是否属于基础类型
// 数字（number）、字符串（string）、布尔值（boolean）、undefined、null、对象（Object）
const isValPrimitive = (value) => {
    const typeStr = Object.prototype.toString.call(value);
    return ['[object Boolean]', '[object Number]', '[object String]'].includes(typeStr);
};
/**
 * 判断类型是否匹配
 * @param {*} typeAnnotation 期望类型
 * @param {*} value 值
 */
const isTypeMatch = (typeKey, value) => {
    const isPrimitive = isDefPrimitive(typeKey); // 类型字符串
    const typeAnnotation = typeDefinitionMap[typeKey];
    const isValuePrimitive = isValPrimitive(value); // 类型字符串
    const typeStr = Object.prototype.toString.call(value);
    const { concept } = typeAnnotation || {};
    let isMatch = isPrimitive === isValuePrimitive || (concept === 'Enum' && typeStr === '[object String]');
    // 大类型匹配的基础上继续深入判断
    if (isMatch) {
        if (isPrimitive) {
            if ((typeKey === 'nasl.core.Boolean' && typeStr !== '[object Boolean]') ||
                (isDefNumber(typeKey) && typeStr !== '[object Number]') ||
                (isDefString(typeKey) && typeStr !== '[object String]')) {
                isMatch = false;
            }
        }
    }
    return isMatch;
};
function unorderedArrayEqual(a, b) {
    return xor(a, b).length === 0;
}
/**
 * 解析类型引用到实际的类型定义
 * @param typeAnnotation 需要解析的类型标注
 * @returns 如果解析成功则返回解析后的类型属性，解析失败返回null
 */
function resolveTypeReference(typeAnnotation) {
    if (typeAnnotation.typeKind === 'reference') {
        const typeKey = genSortedTypeKey(typeAnnotation);
        const candiateDef = getTypeDefinition(typeKey);
        // 如果仍然是type reference，那么视作失败
        if ((candiateDef === null || candiateDef === void 0 ? void 0 : candiateDef.concept) === 'TypeAnnotation' && candiateDef.typeKind === 'reference') {
            return undefined;
        }
        return candiateDef;
    }
    return undefined;
}
function exactMatchShapeAgainstDef(value, def) {
    function isMatchForPrimitive(value, ty) {
        const valueTypeStr = Object.prototype.toString.call(value);
        // 检查enum类型
        if (ty.typeKind === 'primitive' && ty.concept === 'Enum') {
            return valueTypeStr === '[object String]';
        }
        if (ty.typeKind !== 'primitive') {
            return false;
        }
        const typeKey = `${ty.typeNamespace}.${ty.typeName}`;
        return ((typeKey === 'nasl.core.Boolean' && valueTypeStr === '[object Boolean]') ||
            (isDefNumber(typeKey) && valueTypeStr === '[object Number]') ||
            (isDefString(typeKey) && valueTypeStr === '[object String]'));
    }
    if (!def || value === undefined) {
        return false;
    }
    if (value === null) {
        return true;
    }
    if (def.typeKind === 'primitive') {
        return isMatchForPrimitive(value, def);
    }
    else if (def.typeKind === 'union') {
        for (const ty of def.typeArguments) {
            if (exactMatchShapeAgainstDef(value, ty)) {
                return true;
            }
        }
        return false;
    }
    else if (def.typeKind === 'generic') {
        if (def.typeName === 'List' && def.typeNamespace === 'nasl.collection') {
            const targetTy = def.typeArguments[0];
            if (!targetTy || !Array.isArray(value)) {
                return false;
            }
            return value.every((item) => exactMatchShapeAgainstDef(item, targetTy));
        }
        else if (def.typeName === 'Map' && def.typeNamespace === 'nasl.collection') {
            const keyTy = def.typeArguments[0];
            const valueTy = def.typeArguments[1];
            if (!keyTy || !valueTy || typeof value !== 'object' || value === null || Array.isArray(value)) {
                return false;
            }
            for (const [k, v] of Object.entries(value)) {
                if (!exactMatchShapeAgainstDef(k, keyTy) || !exactMatchShapeAgainstDef(v, valueTy)) {
                    return false;
                }
            }
            return true;
        }
        // FIXME 实现一个只看形状的collection匹配函数
        // 在exactMatchShapeAgainstDef中，不应该去调用isInstanceOf。
        // 此时value未被绑定构造器，因此它会返回错误的值。
        return isInstanceOf(value, genSortedTypeKey(def));
    }
    else if (def.properties) {
        // 此时 def 既可以为 anonymousStructure 也可以为 Structure/Entity 等等
        const properties = def.properties;
        if (typeof value === 'object' &&
            value !== null &&
            Array.isArray(properties) &&
            unorderedArrayEqual(Object.keys(value), properties.map((prop) => prop.name))) {
            return properties.every((prop) => {
                const propValue = value === null || value === void 0 ? void 0 : value[prop.name];
                if (propValue === undefined) {
                    return false;
                }
                return exactMatchShapeAgainstDef(propValue, prop.typeAnnotation);
            });
        }
        return false;
    }
    else if (def.typeKind === 'reference') {
        const resolved = resolveTypeReference(def);
        if (resolved) {
            return exactMatchShapeAgainstDef(value, resolved);
        }
    }
    return false;
}
export function getTypeDefinition(typeKey) {
    return typeDefinitionMap[typeKey];
}
function inferConstructorAgainstTypeKey(value, typeKey) {
    var _a;
    const def = getTypeDefinition(typeKey);
    if (!def) {
        return undefined;
    }
    if (def.concept === 'Enum' || def.typeKind === 'primitive') {
        // 视作没有Constructor
        return undefined;
    }
    if (def.typeKind === 'union') {
        // Union的所有分支对应的类型构造器，按照优先级排序后，从左到右匹配：
        // 1. 对当前类型构造器，看它有无定义tag字段：有tag字段且值和value对应字段相同，直接输出当前类型构造器而无需结构一致；否则，跳过。
        // 2. 先判断结构是否一致（复合类型递归看properties属性；Primitive类型直接判断即可）。结构相同的时候，当前类型构造器作为候选；否则，跳过。
        // 输出：第一个候选，或者无匹配
        let candidate = undefined;
        if (!def.typeArguments || !Array.isArray(def.typeArguments) || def.typeArguments.length === 0) {
            return undefined;
        }
        for (const ty of sortTypeArgumentsBasedOnTypePriority(def.typeArguments)) {
            const curTypeKey = `${ty.typeNamespace}.${ty.typeName}`;
            // 对依赖库的extensions.${libname}.errors.${ErrorName}需要映射为extensions.${libname}.structures.${ErrorName}
            const normalizedTypeKey = curTypeKey.startsWith('extensions.')
                ? curTypeKey.replace('.errors.', '.structures.')
                : curTypeKey;
            const curDef = getTypeDefinition(normalizedTypeKey);
            if (!curDef) {
                continue;
            }
            if (curDef.concept === 'Enum' && exactMatchShapeAgainstDef(value, curDef)) {
                return typeMap[curTypeKey];
            }
            else if (ty.typeKind === 'primitive' && exactMatchShapeAgainstDef(value, curDef)) {
                return typeMap[curTypeKey];
            }
            else if (ty.typeKind === 'union') {
                throw new Error('Union类型的typeArguments不能再为union');
            }
            else if (ty.typeKind === 'reference') {
                // Entity | Structure
                // @ts-expect-error FIXME curDef上没有properties
                const properties = curDef === null || curDef === void 0 ? void 0 : curDef.properties;
                if (properties) {
                    // 如果存在字段定义，那么尝试获取第一个tag字段
                    const tagProperty = (_a = flatMap(properties, (prop) => {
                        var _a;
                        const defaultValue = prop.defaultValue;
                        const hardcodedPropertyName = 'errorType';
                        if (prop.name === hardcodedPropertyName && ((_a = defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.expression) === null || _a === void 0 ? void 0 : _a.concept) === 'StringLiteral') {
                            return [
                                // 允许tagValue为undefined
                                { name: prop.name, tagValue: defaultValue.expression.value },
                            ];
                        }
                        return [];
                    })) === null || _a === void 0 ? void 0 : _a[0];
                    if (tagProperty) {
                        if (tagProperty.tagValue === (value === null || value === void 0 ? void 0 : value[tagProperty.name])) {
                            return typeMap[curTypeKey];
                        }
                    }
                    else if (candidate === undefined && exactMatchShapeAgainstDef(value, curDef)) {
                        candidate = typeMap[curTypeKey];
                    }
                }
            }
        }
        return candidate;
    }
    return typeMap[typeKey];
}
/**
 * 初始化变量
 * 基础类型不再进初始化方法
 * @param {*} typeKey
 * @param {*} defaultValue
 * @param {*} parentLevel
 * @returns
 */
export const genInitData = (typeKey, defaultValue, parentLevel) => {
    var _a;
    // 已经实例化过的值，直接返回
    if (isInstanceOf(defaultValue, typeKey)) {
        return defaultValue;
    }
    let level = 1;
    if (parentLevel !== undefined) {
        level = parentLevel + 1;
    }
    const defaultValueType = Object.prototype.toString.call(defaultValue);
    let parsedValue = defaultValue;
    // 设置成null，才能同步给后端清除该值，但是null对checkbox组件是一种特殊状态
    if (typeKey === 'nasl.core.Boolean') {
        parsedValue = defaultValue !== null && defaultValue !== void 0 ? defaultValue : undefined;
    }
    const typeDefinition = typeDefinitionMap[typeKey];
    const { concept, typeKind, typeNamespace, typeName, typeArguments, properties } = typeDefinition || {};
    if (defaultValueType === '[object String]' &&
        !['nasl.core.String', 'nasl.core.Text', 'nasl.core.Email'].includes(typeKey) &&
        concept !== 'Enum' &&
        !['union'].includes(typeKind)) {
        // 一些特殊情况，特殊处理成undefined
        // 1.defaultValue在nasl节点上错误得赋值给了空字符串
        if ([''].includes(defaultValue)) {
            parsedValue = undefined;
        }
        else {
            parsedValue = tryJSONParse(defaultValue) !== undefined ? tryJSONParse(defaultValue) : defaultValue;
        }
    }
    if (level > 2 && [undefined, null].includes(parsedValue)) {
        return;
    }
    // nasl.interface下的类型无法通过构造器构造，因此直接返回
    if ((_a = typeKey === null || typeKey === void 0 ? void 0 : typeKey.startsWith) === null || _a === void 0 ? void 0 : _a.call(typeKey, 'nasl.interface.')) {
        return parsedValue;
    }
    const isTypeMatched = parsedValue === undefined || isTypeMatch(typeKey, parsedValue);
    if (isTypeMatched) {
        if (typeKind === 'generic' && typeNamespace === 'nasl.collection' && ['List', 'Map'].includes(typeName)) {
            // 特殊范型List/Map
            let initVal = typeName === 'List' ? [] : {};
            if (parsedValue) {
                // valueTypeAnnotation可能会由于一些情况出现空，因此不能加上对typeArguments数组的整体容错判断
                const valueTypeAnnotation = typeName === 'List' ? typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[0] : typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[1];
                const sortedTypeKey = genSortedTypeKey(valueTypeAnnotation);
                if (typeName === 'List' && Array.isArray(parsedValue)) {
                    initVal = parsedValue.map((item) => genInitData(sortedTypeKey, item, level));
                }
                else if (typeName === 'Map') {
                    for (const key in parsedValue) {
                        const val = parsedValue[key];
                        initVal[key] = genInitData(sortedTypeKey, val, level);
                    }
                }
            }
            return initVal;
        }
        if (typeName === 'DateTime' && parsedValue !== undefined) {
            if (parsedValue instanceof Date) {
                parsedValue = moment(new Date()).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
            }
            return parsedValue;
        }
        else if (typeKey) {
            const Ctor = inferConstructorAgainstTypeKey(parsedValue, typeKey);
            if (Ctor) {
                if (concept === 'Structure' && Object.prototype.toString.call(parsedValue) === '[object Object]') {
                    parsedValue = jsonNameReflection(properties, parsedValue);
                }
                const instance = new Ctor({
                    defaultValue: parsedValue,
                    level,
                });
                return instance;
            }
        }
    }
    return parsedValue;
};
/**
 * 生成缩进
 * @param tabSize 缩进次数
 * @returns
 */
function indent(tabSize) {
    return ' '.repeat(4 * tabSize);
}
/**
 * 变量转字符串
 * @param {*} variable
 * @param {*} typeKey
 * @param {*} timeZone
 * @param {*} tabSize
 * @param {Set} collection 收集的已处理的对象
 * @returns
 */
export const toString = (typeKey, variable, tz, tabSize = 0, collection = new Set()) => {
    var _a, _b, _c, _d;
    if (variable instanceof Error) {
        return variable;
    }
    // null 或 undefined 返回 "（空）"
    if ([undefined, null].includes(variable) || typeKey === 'nasl.core.Null') {
        // 空
        if (tabSize > 0) {
            return '（空）';
        }
        else {
            return '';
        }
    }
    let str = '';
    const isPrimitive = isDefPrimitive(typeKey);
    if (isPrimitive) {
        // 基础类型
        str = '' + variable;
        // >=8位有效数字时，按小e
        if (['nasl.core.Double', 'nasl.core.Decimal'].includes(typeKey)) {
            const varArr = str.split('.');
            let count = 0;
            varArr.forEach((varStr) => {
                count += varStr.length;
            });
            const maxLen = 8;
            if (count >= maxLen) {
                // 去掉+是为了跟后端保持统一
                // str = (+variable)?.toExponential?.().replace?.('e+', 'e');
                str = variable;
            }
        }
        // 日期时间处理
        if (typeKey === 'nasl.core.Date') {
            str = momentTZ.tz(safeNewDate(variable), getAppTimezone(tz)).format('YYYY-MM-DD');
        }
        else if (typeKey === 'nasl.core.Time') {
            const timeRegex = /^([01]?\d|2[0-3])(?::([0-5]?\d)(?::([0-5]?\d))?)?$/;
            // 纯时间 12:30:00
            if (timeRegex.test(variable)) {
                const match = variable.match(timeRegex);
                const varArr = [];
                const formatArr = [];
                [
                    {
                        index: 1,
                        format: 'HH',
                    },
                    {
                        index: 2,
                        format: 'mm',
                    },
                    {
                        index: 3,
                        format: 'ss',
                    },
                ].forEach(({ index, format }) => {
                    const varItem = match[index];
                    if (varItem) {
                        formatArr.push(format);
                    }
                    varArr.push(varItem || '00');
                });
                str = momentTZ
                    .tz(safeNewDate('2022-01-01 ' + varArr.join(':')), getAppTimezone(tz))
                    .format(formatArr.join(':'));
            }
            else {
                str = momentTZ.tz(safeNewDate(variable), getAppTimezone(tz)).format('HH:mm:ss');
            }
        }
        else if (typeKey === 'nasl.core.DateTime') {
            str = momentTZ.tz(safeNewDate(variable), getAppTimezone(tz)).format('YYYY-MM-DD HH:mm:ss');
        }
        if (tabSize > 0) {
            if (['nasl.core.String', 'nasl.core.Text'].includes(typeKey)) {
                const maxLen = 100;
                const moreThanMax = variable.length > maxLen;
                if (moreThanMax) {
                    str = variable.slice(0, maxLen) + '...';
                }
            }
            // 是否属于字符串大类
            if (isDefString(typeKey)) {
                str = `"${str}"`;
            }
        }
    }
    else {
        const typeDefinition = typeDefinitionMap[typeKey];
        let { concept, typeKind, typeNamespace, typeName, typeArguments, name, properties, enumItems } = typeDefinition || {};
        if (typeKind === 'union') {
            if (Array.isArray(typeArguments) && typeArguments.length) {
                const typeArg = typeArguments.find((typeArg) => isInstanceOf(variable, genSortedTypeKey(typeArg)));
                if (typeArg) {
                    str = toString(genSortedTypeKey(typeArg), variable, tz, tabSize, collection);
                }
            }
        }
        else if (concept === 'Enum') {
            if (Array.isArray(enumItems) && enumItems.length) {
                // 改为不严格判断，枚举值支持数字类型
                const enumItem = enumItems.find((enumItem) => variable == enumItem.value);
                if (window.$i18n && ((_b = (_a = window.$global) === null || _a === void 0 ? void 0 : _a.i18nInfo) === null || _b === void 0 ? void 0 : _b.enabled) && ((_c = enumItem === null || enumItem === void 0 ? void 0 : enumItem.label) === null || _c === void 0 ? void 0 : _c.i18nKey)) {
                    str = window.$i18n.t(enumItem.label.i18nKey);
                }
                else {
                    str = ((_d = enumItem === null || enumItem === void 0 ? void 0 : enumItem.label) === null || _d === void 0 ? void 0 : _d.value) || '';
                }
            }
        }
        else if (['TypeAnnotation', 'Structure', 'Entity'].includes(concept)) {
            // 复合类型
            if (collection.has(variable)) {
                str = '';
                if (isDefList(typeDefinition)) {
                    if (variable.length > 0) {
                        str += '[...]';
                    }
                    else {
                        str += '[]';
                    }
                }
                else if (isDefMap(typeDefinition)) {
                    const keys = Object.keys(variable);
                    if (keys.length > 0) {
                        str += '[... -> ...]';
                    }
                    else {
                        str += '[->]';
                    }
                }
                else {
                    const keys = Object.keys(variable);
                    if (name) {
                        str += `${name} `;
                    }
                    if (keys.length > 0) {
                        str += '{...}';
                    }
                    else {
                        str += '{}';
                    }
                }
            }
            else {
                collection.add(variable);
                if (typeKind === 'generic' && typeNamespace === 'nasl.collection') {
                    if (typeName === 'List') {
                        const itemTypeKey = genSortedTypeKey(typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[0]);
                        const tmp = variable.map((varItem) => `${indent(tabSize + 1)}${toString(itemTypeKey, varItem, tz, tabSize + 1, collection)}`);
                        const arrStr = tmp.join(',\n');
                        if (variable.length) {
                            str = `[\n${arrStr}\n${indent(tabSize)}]`;
                        }
                        else {
                            str = '[]';
                        }
                    }
                    else if (typeName === 'Map') {
                        const keys = Object.keys(variable);
                        const keyTypeKey = genSortedTypeKey(typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[0]);
                        const itemTypeKey = genSortedTypeKey(typeArguments === null || typeArguments === void 0 ? void 0 : typeArguments[1]);
                        const arrStr = keys
                            .map((key) => `${indent(tabSize + 1)}${toString(keyTypeKey, key, tz, tabSize + 1, collection)} -> ${toString(itemTypeKey, variable[key], tz, tabSize + 1, collection)}`)
                            .join(',\n');
                        if (keys.length) {
                            str = `{\n${arrStr}\n${indent(tabSize)}}`;
                        }
                        else {
                            str = '{}';
                        }
                    }
                }
                else {
                    // 处理一些范型数据结构的情况
                    if (typeKind === 'generic') {
                        const genericTypeKey = `${typeNamespace}.${typeName}`;
                        const genericTypeDefinition = typeDefinitionMap[genericTypeKey];
                        if (genericTypeDefinition) {
                            name = genericTypeDefinition === null || genericTypeDefinition === void 0 ? void 0 : genericTypeDefinition.name;
                            const genericProperties = (genericTypeDefinition === null || genericTypeDefinition === void 0 ? void 0 : genericTypeDefinition.properties) || [];
                            if (Array.isArray(genericTypeDefinition.typeParams) && genericTypeDefinition.typeParams.length) {
                                const map = {};
                                genericTypeDefinition.typeParams.forEach((typeParam, index) => {
                                    const { name } = typeParam || {};
                                    map[name] = index;
                                });
                                properties = genericProperties.map((genericProperty) => {
                                    let typeAnnotation = genericProperty === null || genericProperty === void 0 ? void 0 : genericProperty.typeAnnotation;
                                    const { typeName } = typeAnnotation || {};
                                    const typeParamIndex = map[typeName];
                                    if (typeParamIndex !== undefined) {
                                        typeAnnotation = typeArguments[typeParamIndex];
                                    }
                                    return Object.assign(Object.assign({}, genericProperty), { typeAnnotation });
                                });
                            }
                        }
                    }
                    let code = '';
                    if (name) {
                        code += `${name} `;
                    }
                    code += '{\n';
                    if (Array.isArray(properties) && properties.length) {
                        code += properties
                            .map((property) => {
                            const { name: propName, typeAnnotation: propTypeAnnotation } = property || {};
                            const propVal = variable[propName];
                            const propTypeKey = genSortedTypeKey(propTypeAnnotation);
                            const propValStr = toString(propTypeKey, propVal, tz, tabSize + 1, collection);
                            return `${indent(tabSize + 1)}${propName}: ${propValStr}`;
                        })
                            .join(',\n');
                    }
                    code += `\n${indent(tabSize)}}`;
                    str = code;
                }
            }
        }
    }
    if (str === '') {
        if (Object.prototype.toString.call(variable) === '[object Object]') {
            if (collection.has(variable)) {
                str = '{...}';
            }
            else {
                collection.add(variable);
                str = `{\n`;
                const propStr = [];
                for (const key in variable) {
                    const propVal = variable[key];
                    const propValStr = toString(undefined, propVal, tz, tabSize + 1, collection);
                    propStr.push(`${indent(tabSize + 1)}${key}: ${propValStr}`);
                }
                str += propStr.join(',\n');
                str += `\n}`;
            }
        }
        else {
            str = '' + variable;
        }
    }
    return str;
};
// yyyy-MM-dd HH:mm:ss
// yyyy/MM/dd HH:mm:ss
// yyyy.MM.dd HH:mm:ss
const TimeReg = /^(20|21|22|23|[0-1]\d):[0-5]\d:[0-5]\d$/;
const FloatNumberReg = /^(-?\d+)(\.\d+)?$/;
// (长)整型
const IntegerReg = /^-?\d+$/;
/**
 * 判断字符串日期是否合法
 * yyyy-MM-dd yyyy/MM/dd HH:mm:ss yyyy.MM.dd 3种格式
 * @param {*} dateString
 * @returns
 */
function isValidDate(dateString, reg) {
    var _a, _b;
    if (!reg.test(dateString)) {
        return false;
    }
    // 验证日期是否真实存在
    const date = safeNewDate(dateString);
    if (date.toString() === 'Invalid Date') {
        return false;
    }
    let splitChar;
    if (dateString.includes('-')) {
        splitChar = '-';
    }
    else if (dateString.includes('/')) {
        splitChar = '/';
    }
    else if (dateString.includes('.')) {
        splitChar = '.';
    }
    const [year, month, day] = (_b = (_a = dateString.split(' ')) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.split(splitChar).map(Number);
    if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
        return false;
    }
    return true;
}
export const fromString = (variable, typeKey) => {
    const typeDefinition = typeDefinitionMap[typeKey];
    const isPrimitive = isDefPrimitive(typeKey);
    const { typeName } = typeDefinition || {};
    // 日期
    if (typeName === 'DateTime') {
        const date = safeNewDate(variable);
        const outputDate = format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
        return outputDate;
    }
    else if (typeName === 'Date') {
        return moment(safeNewDate(variable)).format('YYYY-MM-DD');
    }
    else if (typeName === 'Time' && TimeReg.test(variable)) {
        // ???
        return moment(safeNewDate('2022-01-01 ' + variable)).format('HH:mm:ss');
    }
    // 浮点数
    else if (['Decimal', 'Double'].includes(typeName) && FloatNumberReg.test(variable)) {
        return parseFloat(String(+variable));
    }
    // 整数
    else if (['Integer', 'Long'].includes(typeName) && IntegerReg.test(variable)) {
        const maxMap = {
            Integer: 2147483647,
            Long: 9223372036854775807,
        };
        const numberVar = +variable;
        if (numberVar < maxMap[typeName] && numberVar > -maxMap[typeName]) {
            return numberVar;
        }
    }
    // 布尔
    else if (typeName === 'Boolean') {
        if (['true', 'false'].includes(variable)) {
            return JSON.parse(variable);
        }
    }
    toastAndThrowError(`${typeName}格式不正确`);
};
export function toastAndThrowError(err) {
    // 全局提示toast
    Config.toast.error(err);
    throw new Error(err);
}
function jsonNameReflection(properties, parsedValue) {
    if (!Array.isArray(properties))
        return parsedValue;
    properties.forEach(({ jsonName, name }) => {
        if (jsonName === name || jsonName === undefined || jsonName === null || jsonName === '')
            return;
        parsedValue[name] = parsedValue[jsonName];
        delete parsedValue[jsonName];
    });
    return parsedValue;
}
